// yubiley-korporativ-preview — Corporate anniversary catalog card.
//
// Mood: stately corporate jubilee in deep navy + champagne gold. The
// outer frame is a polished gold gradient with crisp hairlines; the
// inner card sits on the template's near-black paper with ivory ink
// and a giant Roman-numeral / star medallion centred. Eyebrow in
// Uzbek: "YUBILEY".
import { type LayoutCtx } from "./_shared";

export function YubileyKorporativPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  // Polished champagne-gold foil for corporate gravitas.
  const FOIL =
    "linear-gradient(135deg, #8A7340 0%, #C9B07A 24%, #EBD9A6 50%, #C9B07A 76%, #8A7340 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          YUBILEY
        </div>

        {/* Star / sunburst medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            {/* Double hairline */}
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.55" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="50" fill="none" stroke={accent} strokeOpacity="0.3" strokeWidth="0.4" />

            {/* 16-point sunburst */}
            {Array.from({ length: 16 }, (_, i) => {
              const angle = (i * 360) / 16;
              return (
                <g key={i} transform={`translate(60 60) rotate(${angle})`}>
                  <path
                    d="M -1.6 0 L 0 -46 L 1.6 0 Z"
                    fill={accent}
                    fillOpacity={i % 2 ? 0.45 : 0.75}
                  />
                </g>
              );
            })}

            {/* Inner laurel ring + central star */}
            <circle cx="60" cy="60" r="20" fill="none" stroke={accent} strokeOpacity="0.55" strokeWidth="0.6" />
            <g transform="translate(60 60)">
              <path
                d="M0 -14 L3.4 -4.4 L13.3 -4.4 L5.3 1.7 L8.4 11.3 L0 5.6 L-8.4 11.3 L-5.3 1.7 L-13.3 -4.4 L-3.4 -4.4 Z"
                fill={accent}
                fillOpacity="0.95"
              />
            </g>
          </svg>
        </div>

        {/* Names — for corporate this often is the company / event name. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 28,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
            letterSpacing: "0.02em",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          YUBILEY · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
